// RegisterPage — Phase 12a: 3-option account-type step (HOST / ATTENDEE / PROFESSIONAL)
import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { ArrowLeft, User, Mail, Phone, Lock, Shield, Briefcase, Camera, Calendar, CheckCircle, Circle } from 'lucide-react';

const ACCOUNT_TYPE_OPTIONS = [
  { key: 'HOST', title: 'Host', hint: 'I create events and collect memories', Icon: Calendar },
  { key: 'ATTENDEE', title: 'Attendee', hint: 'I attend events & share my photos', Icon: Camera },
  { key: 'PROFESSIONAL', title: 'Professional / Business', hint: 'I host events for clients (DJ, planner, venue\u2026)', Icon: Briefcase },
];

const BIZ_CATS = [
  { key: 'PHOTOGRAPHER', label: 'Photographer' },
  { key: 'WEDDING_EVENT_PLANNER', label: 'Wedding / Event Planner' },
  { key: 'DJ', label: 'DJ' },
  { key: 'VENUE', label: 'Venue' },
  { key: 'PARTY_SERVICES', label: 'Party Services' },
  { key: 'OTHER', label: 'Other' },
];

export default function RegisterPage() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const inviteToken = sp.get('token') || sp.get('event') || '';
  const refCode = sp.get('ref') || '';
  const skipAccountType = !!inviteToken;

  const [step, setStep] = useState(skipAccountType ? 'details' : 'account-type');
  const [accountType, setAccountType] = useState('ATTENDEE');
  const [bizName, setBizName] = useState('');
  const [bizCat, setBizCat] = useState('');

  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [pw, setPw] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const isPro = accountType === 'PROFESSIONAL';
  const totalSteps = skipAccountType ? 1 : isPro ? 3 : 2;
  const stepIndex = step === 'account-type' ? 0 : step === 'business' ? 1 : (skipAccountType ? 0 : isPro ? 2 : 1);

  function goForward() {
    setError(null);
    if (isPro) setStep('business');
    else setStep('details');
  }

  function confirmBusiness() {
    if (!bizName.trim()) { setError('Business name is required.'); return; }
    if (!bizCat) { setError('Please choose a category.'); return; }
    setError(null);
    setStep('details');
  }

  function goBack() {
    if (step === 'details' && isPro && !skipAccountType) setStep('business');
    else if (step === 'business') setStep('account-type');
    else navigate(-1);
  }

  function validate() {
    if (!first.trim() || !last.trim()) return 'Please enter your first and last name.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Enter a valid email address.';
    if (!/^\+?[0-9()\s.-]{8,20}$/.test(phone)) return 'Enter a valid phone number.';
    if (pw.length < 8) return 'Password must be at least 8 characters.';
    return null;
  }

  async function submit(e) {
    e.preventDefault();
    setError(null);
    const v = validate();
    if (v) { setError(v); return; }
    setSubmitting(true);
    try {
      const payload = {
        first_name: first.trim(), last_name: last.trim(),
        email: email.trim().toLowerCase(), phone: phone.trim(), password: pw,
        account_type: accountType,
      };
      if (isPro && bizName.trim()) payload.business_name = bizName.trim();
      if (isPro && bizCat) payload.business_category = bizCat;
      if (refCode) payload.referral_code = refCode;
      if (inviteToken) payload.invite_token = inviteToken;

      const body = await apiFetch('/api/v1/auth/register', {
        method: 'POST', auth: false,
        body: JSON.stringify(payload),
      });
      const params = new URLSearchParams({
        registration_id: body.registration_id,
        email_masked: body.email_masked,
        phone_masked: body.phone_masked,
        dev_code: body.dev_code_email || '',
        simulated: body.simulated ? '1' : '0',
      });
      navigate(`/verify-email?${params.toString()}`);
    } catch (err) {
      if (err.code === 'RATE_LIMITED') setError(`Too many attempts. Try again in ${err.details?.retry_after_seconds ?? 'a few'}s.`);
      else setError(err.message || 'Registration failed.');
    } finally { setSubmitting(false); }
  }

  const submitLabel = isPro ? 'Create Professional Account' : accountType === 'HOST' ? 'Create Host Account' : 'Create Account';

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4" data-testid="register-screen">
      <div className="w-full max-w-md">
        <button onClick={goBack} className="flex items-center gap-1 text-[#146BFF] font-semibold text-sm mb-4 hover:underline" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> back
        </button>

        <div className="mb-1 flex items-center gap-2">
          <img src="/allviews-mark.svg" alt="" className="h-5 w-5" />
          <img src="/allviews-wordmark.svg" alt="ALLVIEWS" className="h-3.5" />
          <span className="text-xs text-[#5B6472] ml-1">Create Account</span>
        </div>

        {/* ─── Step: Account type (3 cards) ─── */}
        {step === 'account-type' && (
          <div data-testid="register-step-account-type">
            <div className="flex items-center justify-between mt-6 mb-2">
              <h1 className="text-2xl font-extrabold text-[#0B1B46]">How will you use ALLVIEWS?</h1>
              <StepBadge n={1} total={totalSteps} />
            </div>
            <ProgressDots current={0} total={totalSteps} />
            <p className="text-sm text-[#5B6472] mb-5">This helps us personalize your experience. You can always change it later.</p>

            <div className="flex flex-col gap-3">
              {ACCOUNT_TYPE_OPTIONS.map(({ key, title, hint, Icon }) => {
                const active = accountType === key;
                return (
                  <button key={key} type="button" onClick={() => setAccountType(key)}
                    className={`flex items-center gap-3 p-3.5 rounded-xl border-2 text-left transition-all ${active ? 'border-[#146BFF] bg-[#EFF5FF]' : 'border-[#E3E8EF] bg-white hover:border-[#B8CFFF]'}`}
                    data-testid={`register-account-type-${key.toLowerCase()}`}>
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 border transition-colors ${active ? 'bg-[#146BFF] border-[#146BFF]' : 'bg-[#F3F5F8] border-[#E3E8EF]'}`}>
                      <Icon className={`w-5 h-5 ${active ? 'text-white' : 'text-[#146BFF]'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-[15px] font-extrabold ${active ? 'text-[#146BFF]' : 'text-[#0B1B46]'}`}>{title}</p>
                      <p className="text-[13px] text-[#5B6472] leading-tight">{hint}</p>
                      {key === 'HOST' && <p className="text-xs text-[#8F9BB0] leading-tight mt-0.5">Most hosts start as Attendees and upgrade later.</p>}
                    </div>
                    {active ? <CheckCircle className="w-5 h-5 text-[#146BFF] flex-shrink-0" /> : <Circle className="w-5 h-5 text-[#C6CDD5] flex-shrink-0" />}
                  </button>
                );
              })}
            </div>

            <button onClick={goForward} className="w-full h-11 mt-6 bg-[#146BFF] hover:bg-[#0F55CC] text-white font-semibold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-[#146BFF] focus:ring-offset-2" data-testid="register-account-type-continue">
              Continue
            </button>

            <div className="mt-4 text-center text-sm text-[#5B6472]">
              Already have an account?{' '}
              <Link to="/sign-in" className="text-[#146BFF] font-bold hover:underline" data-testid="reg-to-sign-in-btn-top">Sign In</Link>
            </div>
          </div>
        )}

        {/* ─── Step: Business details ─── */}
        {step === 'business' && (
          <div data-testid="register-step-business">
            <div className="flex items-center justify-between mt-6 mb-2">
              <h1 className="text-2xl font-extrabold text-[#0B1B46]">About your business</h1>
              <StepBadge n={2} total={totalSteps} />
            </div>
            <ProgressDots current={1} total={totalSteps} />
            <p className="text-sm text-[#5B6472] mb-4">We&apos;ll show your business name on the lookbook. Personalized pricing is based on your category.</p>
            <div className="flex flex-col gap-4">
              <div>
                <label htmlFor="biz-name" className="block text-sm font-semibold text-[#0B1B46] mb-1.5">Business Name</label>
                <input id="biz-name" placeholder="e.g. Sarah Foto Co." value={bizName} onChange={e => setBizName(e.target.value)}
                  className="w-full h-11 px-3 border border-[#E3E8EF] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#146BFF] bg-white" data-testid="reg-biz-name" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#0B1B46] mb-1.5">Business Category</label>
                <div className="flex flex-wrap gap-2" data-testid="reg-biz-categories">
                  {BIZ_CATS.map(c => (
                    <button key={c.key} type="button" onClick={() => setBizCat(c.key)}
                      className={`px-3 py-2 rounded-full text-sm font-semibold border transition-colors ${bizCat === c.key ? 'bg-[#146BFF] text-white border-[#146BFF]' : 'bg-white text-[#0B1B46] border-[#E3E8EF] hover:bg-[#F3F5F8]'}`}
                      data-testid={`reg-biz-cat-${c.key.toLowerCase()}`}>{c.label}</button>
                  ))}
                </div>
              </div>
              {error && <div className="bg-[#FBE5E5] border border-[#C93434]/20 rounded-lg p-3 text-sm text-[#C93434]" data-testid="register-error">{error}</div>}
              <button onClick={confirmBusiness} className="h-11 bg-[#146BFF] hover:bg-[#0F55CC] text-white font-semibold rounded-lg transition-colors" data-testid="reg-biz-continue">Continue</button>
            </div>
          </div>
        )}

        {/* ─── Step: Personal details ─── */}
        {step === 'details' && (
          <div data-testid="register-step-details">
            <div className="flex items-center justify-between mt-6 mb-2">
              <div>
                <h1 className="text-2xl font-extrabold text-[#0B1B46] mb-1" data-testid="screen-title">Create Account</h1>
                <p className="text-sm text-[#5B6472]">Relive every moment through the people who were there.</p>
              </div>
              <StepBadge n={stepIndex + 1} total={totalSteps} />
            </div>
            <ProgressDots current={stepIndex} total={totalSteps} />

            <form onSubmit={submit} className="flex flex-col gap-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="reg-first" className="block text-sm font-semibold text-[#0B1B46] mb-1.5">First Name</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5B6472]" />
                    <input id="reg-first" placeholder="First name" autoComplete="given-name" value={first} onChange={e => setFirst(e.target.value)}
                      className="w-full h-11 pl-10 pr-3 border border-[#E3E8EF] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#146BFF] bg-white" data-testid="reg-first-name" />
                  </div>
                </div>
                <div>
                  <label htmlFor="reg-last" className="block text-sm font-semibold text-[#0B1B46] mb-1.5">Last Name</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5B6472]" />
                    <input id="reg-last" placeholder="Last name" autoComplete="family-name" value={last} onChange={e => setLast(e.target.value)}
                      className="w-full h-11 pl-10 pr-3 border border-[#E3E8EF] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#146BFF] bg-white" data-testid="reg-last-name" />
                  </div>
                </div>
              </div>
              <div>
                <label htmlFor="reg-email" className="block text-sm font-semibold text-[#0B1B46] mb-1.5">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5B6472]" />
                  <input id="reg-email" type="email" placeholder="Enter your email" autoComplete="email" value={email} onChange={e => setEmail(e.target.value)}
                    className="w-full h-11 pl-10 pr-3 border border-[#E3E8EF] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#146BFF] bg-white" data-testid="reg-email" />
                </div>
              </div>
              <div>
                <label htmlFor="reg-phone" className="block text-sm font-semibold text-[#0B1B46] mb-1.5">Mobile Phone</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5B6472]" />
                  <input id="reg-phone" type="tel" placeholder="+1 555 123 4567" autoComplete="tel" value={phone} onChange={e => setPhone(e.target.value)}
                    className="w-full h-11 pl-10 pr-3 border border-[#E3E8EF] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#146BFF] bg-white" data-testid="reg-phone" />
                </div>
              </div>
              <div>
                <label htmlFor="reg-pw" className="block text-sm font-semibold text-[#0B1B46] mb-1.5">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5B6472]" />
                  <input id="reg-pw" type="password" placeholder="At least 8 characters" autoComplete="new-password" value={pw} onChange={e => setPw(e.target.value)}
                    className="w-full h-11 pl-10 pr-3 border border-[#E3E8EF] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#146BFF] bg-white" data-testid="reg-password" />
                </div>
              </div>

              {refCode && (
                <div className="bg-[#EFF5FF] border border-[#D6E4FF] rounded-lg p-3 text-sm text-[#0B1B46]" data-testid="register-ref-banner">
                  Referred by code <span className="font-extrabold">{refCode}</span> &mdash; welcome to ALLVIEWS.
                </div>
              )}

              {error && <div className="bg-[#FBE5E5] border border-[#C93434]/20 rounded-lg p-3 text-sm text-[#C93434]" data-testid="register-error">{error}</div>}

              <button type="submit" disabled={submitting}
                className="h-11 bg-[#146BFF] hover:bg-[#0F55CC] disabled:opacity-50 text-white font-semibold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-[#146BFF] focus:ring-offset-2"
                data-testid="reg-submit-btn">
                {submitting ? 'Creating\u2026' : submitLabel}
              </button>
            </form>

            <div className="mt-4 text-center text-sm text-[#5B6472]">
              Already have an account?{' '}
              <Link to="/sign-in" className="text-[#146BFF] font-bold hover:underline" data-testid="reg-to-sign-in-btn">Sign In</Link>
            </div>

            <div className="mt-6 bg-[#EFF5FF] border border-[#D6E4FF] rounded-xl p-4" data-testid="register-trust-card">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-[#146BFF] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-bold text-[#0B1B46] mb-1">Verified contributors are required</p>
                  <p className="text-sm text-[#5B6472]">This helps us keep events secure and ensures a trusted experience for everyone.</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StepBadge({ n, total }) {
  return <span className="bg-[#EFF5FF] text-[#146BFF] text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">Step {n} of {total}</span>;
}
function ProgressDots({ current, total }) {
  return <div className="flex gap-2 mb-6" data-testid="register-progress">{Array.from({ length: total }, (_, i) => <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= current ? 'bg-[#146BFF]' : 'bg-[#E3E8EF]'}`} />)}</div>;
}
